/**
 * hoodieApi services — Reference Django backend (themamahood).
 * Use these for pages wired to the hoodie API. For the main app backend use services/api.js (mainApi).
 * Response shapes follow the backend (DRF serializers, LimitOffsetPagination).
 */

import { hoodieFetchJson, isHoodieApiConfigured } from "../hoodieClient";

function buildQueryString(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => search.append(key, v));
    } else {
      search.append(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? "?" + qs : "";
}

/**
 * List directory entries (M List). Returns { count, next, previous, results }.
 * Backend (DirectoryEntryViewSet) supports: search, tags (ids), tag_slug (slugs), main_tag_slug, under_grouping (grouping id), sort=featured, limit, offset.
 * @param {{ limit?: number, offset?: number, search?: string, tag_slug?: string[], main_tag_slug?: string, under_grouping?: number, tags?: number[], sort?: string }} params
 */
export async function getListings(params = {}) {
  const path = "listings/" + buildQueryString(params);
  const { data } = await hoodieFetchJson(path);
  return data;
}

/**
 * Single directory entry by id.
 */
export async function getListingById(id) {
  const { data } = await hoodieFetchJson(`listings/${id}/`);
  return data;
}

/**
 * Patch directory entry (safe, serializer-supported fields only).
 * @param {number|string} id
 * @param {Record<string, any>} patch
 */
export async function patchListingById(id, patch) {
  const { data, response } = await hoodieFetchJson(`listings/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(patch || {}),
  });
  return { data, status: response.status };
}

/**
 * Tags. Query params: main_tag_slug, main (for main-only).
 */
export async function getTags(params = {}) {
  const path = "tags/" + buildQueryString(params);
  const { data } = await hoodieFetchJson(path);
  return data;
}

/**
 * Tag groupings (categories).
 */
export async function getTagGroupings() {
  const { data } = await hoodieFetchJson("tag-groupings/");
  return data;
}

/**
 * Upcoming events (no pagination in backend).
 * Backend (ListingEventViewSet) supports: search (name, entry__business_name, hosted_by, location, short_description, full_description).
 * @param {{ search?: string }} params
 */
export async function getEvents(params = {}) {
  const path = "events/" + buildQueryString(params);
  const { data } = await hoodieFetchJson(path);
  return data;
}

/**
 * Active promos (no pagination in backend).
 */
export async function getPromos() {
  const { data } = await hoodieFetchJson("promos/");
  return data;
}

/**
 * Social groups.
 */
export async function getSocialGroups() {
  const { data } = await hoodieFetchJson("social-groups/");
  return data;
}

/**
 * Current user's hoodie profile (requires session auth).
 */
export async function getMyProfile() {
  const { data } = await hoodieFetchJson("hoodies/me/");
  return data;
}

/**
 * Hoodie profiles list. Note: legacy IsOwnProfileOrUnauthorized blocks list for most users.
 * @param {{ limit?: number, offset?: number }} params
 */
export async function getHoodies(params = {}) {
  const path = "hoodies/" + buildQueryString(params);
  const { data } = await hoodieFetchJson(path);
  return data;
}

/**
 * Single hoodie profile by id (own profile only unless staff overrides permissions).
 */
export async function getHoodieById(id) {
  const { data } = await hoodieFetchJson(`hoodies/${id}/`);
  return data;
}

/**
 * Auth user by id (own user only per IsUserDetailOrUnauthorized).
 */
export async function getUserById(id) {
  const { data } = await hoodieFetchJson(`users/${id}/`);
  return data;
}

export { isHoodieApiConfigured };
